/* Filtrous quantity-stepper skin - v1.2, 2026-08-26 (Adelaide, looking at a
   column of steppers on the parts table: "is there a way to make this more
   beautiful while adhering to accessibility guidelines" / "accessibility
   comes first although it still has to be aesthetically pleasing to the eye
   and it should be visually consistent").
   The site had FOUR quantity steppers with three different looks: the parts
   table / listing rows (.fv3-stepper), the product cards and protocol rail
   (.pc-step), the PDP buy block (.fv3-buy .fv3-step) and this project's own
   cart drawer + quick view (.fcart-q, .fqv-step). The listing one drew its
   value as a WHITE input with a forced 1px border sitting inside GREY
   buttons inside the bordered pill - three nested rectangles for one number,
   which is what read as fussy repeated down every row of a table.
   BEAUTY + CONSISTENCY: all five now wear one treatment - a single pill,
   transparent segments, the value in tabular numerals so it never shifts
   width as it changes, and colour reserved for states that mean something
   (teal on hover, active and focus). Nothing nests, so a column of them
   reads as one clean rhythm, and the same control looks the same wherever
   it appears.
   ACCESSIBILITY FIRST. What was actually wrong, measured on the page:
   - FOCUS RING CLIPPED (WCAG 2.4.7 / 2.4.11): the ring itself was already
     there from the existing a11y CSS (3px teal, ~4:1), but the pill set
     overflow:hidden, so the ring around the first and last segments was cut
     off at the pill's edge. Overflow is now open and the ring draws in full.
     (An earlier note in this file claimed there was NO focus indicator -
     that was wrong: it came from measuring a programmatic .focus(), which
     does not match :focus-visible. Corrected here so the next reader is not
     misled.)
   - BOUNDARY CONTRAST (WCAG 1.4.11): the pill border was #e3e9ee, measured
     1.22:1 against the page, well under the 3:1 asked of a control
     boundary. It is now #8894a1, measured 3.09:1, still a hairline.
   Also: targets are at least 28x28, above the 24x24 of WCAG 2.5.8 (the
   buy-column override had squeezed them to 22px wide); the value announces
   changes (aria-live=polite); and the +/- buttons inherit the row's own
   identity - the app already names the input "Quantity for CS4501", so the
   buttons become "Decrease quantity for CS4501" instead of sixteen identical
   "Decrease quantity" buttons on one table. Glyph contrast was already ~13:1
   and is untouched. Colour transitions are gated behind prefers-reduced-motion.
   WHY !important AND WHY THE SELECTORS LOOK FUSSY (v1.1 fixed both):
   - The app injects its own stepper CSS at runtime, AFTER this stylesheet,
     and filtrous-utilities.css forces `body input.fv3-qty {border-width:1px
     !important}` - the box around the number. Overriding vendor rules we do
     not own is the one place !important is the honest tool.
   - `.fv3-step` names TWO different things in the app: the pill wrapper in
     the PDP buy block, and the BUTTONS inside a listing .fv3-stepper. Each
     is therefore addressed through its own container, never bare.
   - v1 built its state rules as `list + ':hover'`, which attaches the pseudo
     to the LAST selector only - so every other stepper got the hover and
     focus styling permanently, including a teal ring at rest. States are now
     expanded per selector.
   Purely additive: this asset only restyles and re-labels controls the app
   already renders - no markup is replaced and no click behaviour is touched,
   so quantities, adds and removes work exactly as before. Remove its loader
   line in snippets/filtrous-cart-drawer.liquid to revert. */
(function () {
  'use strict';
  if (window.__fstepSkin) return;
  window.__fstepSkin = true;

  /* every stepper on the site: [pill, its segments, its value] */
  var PILLS = [
    '#fv3-app .fv3-stepper',
    '#fv3-app .pc-step',
    '#fv3-app .fv3-buy .fv3-step',
    '#fcart .fcart-q',
    '.fqv-step'
  ];
  var SEGS = [
    '#fv3-app .fv3-stepper .fv3-step',
    '#fv3-app .pc-step button',
    '#fv3-app .fv3-buy .fv3-step button',
    '#fcart .fcart-q button',
    '.fqv-step button'
  ];
  var VALS = [
    '#fv3-app .fv3-stepper input.fv3-qty',
    '#fv3-app .pc-step b',
    '#fv3-app .fv3-buy .fv3-step b',
    '#fcart .fcart-q b',
    '.fqv-step b'
  ];
  /* attach a pseudo-class to EVERY selector, not just the last one */
  function each(list, suffix) {
    return list.map(function (s) { return s + (suffix || ''); }).join(',');
  }

  var css = document.createElement('style');
  css.textContent =
    /* one pill: no nested boxes, and open overflow so the focus ring is never clipped */
    each(PILLS) + '{border:1px solid #8894a1 !important;border-radius:9px !important;background:#fff !important;overflow:visible !important}' +
    /* segments: transparent, generous target, no inner fills */
    each(SEGS) + '{background:transparent !important;border:0 !important;color:#0C2D4A !important;font-family:inherit;font-size:15px;font-weight:600;line-height:1;cursor:pointer;min-width:28px;min-height:28px;padding:0;border-radius:8px}' +
    each(SEGS, ':hover:not(:disabled)') + '{background:#e9f7f4 !important;color:#0d7d68 !important}' +
    each(SEGS, ':active:not(:disabled)') + '{background:#d8f1eb !important}' +
    each(SEGS, ':disabled') + '{opacity:.4;cursor:default}' +
    each(SEGS, ':focus-visible') + '{outline:2px solid #0d7d68;outline-offset:2px;position:relative;z-index:1}' +
    /* the value: no box of its own, fixed-width digits. The !important on the
       border beats filtrous-utilities.css forcing one onto input.fv3-qty. */
    each(VALS) + '{background:transparent !important;border:0 !important;box-shadow:none !important;font-weight:600;color:#0C2D4A !important;font-variant-numeric:tabular-nums;text-align:center}' +
    '#fv3-app .fv3-stepper input.fv3-qty:focus-visible{outline:2px solid #0d7d68;outline-offset:2px}' +
    '@media (prefers-reduced-motion: no-preference){' + each(SEGS) + '{transition:background-color .12s ease,color .12s ease}}' +
    /* the buy column had squeezed its segments below the 24px minimum */
    '#fv3-app .fv3-buy .fv3-pcard .pc-step button{min-width:26px;width:auto}' +
    '#fv3-app .fv3-buy .fv3-pcard .pc-step b{min-width:28px}';
  document.head.appendChild(css);

  /* give the +/- buttons the row's identity, and let the value announce
     itself. Idempotent - marked steppers are skipped on re-render. */
  function clean(s) { return String(s || '').replace(/\s+/g, ' ').trim(); }
  function subjectOf(wrap) {
    /* the app already names the input, e.g. "Quantity for CS4501" */
    var input = wrap.querySelector('input.fv3-qty');
    var lab = input && input.getAttribute('aria-label');
    var fromInput = lab ? clean(lab).replace(/^quantity\s+(for|of)\s+/i, '') : '';
    if (fromInput && fromInput.toLowerCase() !== 'quantity') return fromInput;
    /* otherwise the first product link in the row that actually has text */
    var row = wrap.closest('tr, li, .fv3-pcard') || wrap.parentElement;
    for (var hop = 0; row && hop < 4; hop++) {
      var links = row.querySelectorAll('a[href*="/products/"]');
      for (var i = 0; i < links.length; i++) {
        var txt = clean(links[i].textContent);
        if (txt) return txt;
      }
      row = row.parentElement;
    }
    return '';
  }
  function labelAll() {
    var pills = document.querySelectorAll(
      '#fv3-app .fv3-stepper:not([data-fstep]), #fv3-app .pc-step:not([data-fstep])'
    );
    [].slice.call(pills).forEach(function (wrap) {
      wrap.setAttribute('data-fstep', '1');
      var val = wrap.querySelector('b');
      if (val && !val.hasAttribute('aria-live')) val.setAttribute('aria-live', 'polite');
      var subject = subjectOf(wrap);
      if (!subject) return;
      var btns = wrap.querySelectorAll('button');
      if (btns[0]) btns[0].setAttribute('aria-label', 'Decrease quantity for ' + subject);
      if (btns[1]) btns[1].setAttribute('aria-label', 'Increase quantity for ' + subject);
    });
  }

  function boot() {
    labelAll();
    var host = document.getElementById('fv3-app') || document.body;
    try { new MutationObserver(labelAll).observe(host, { childList: true, subtree: true }); } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
